/**
 * Site Switcher - Multi-site support for Task Automation Platform
 */

// Site configurations
const SITE_CONFIGS = {
    genspark: {
        id: 'genspark',
        name: 'Genspark',
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>',
        description: 'AI 이미지 생성',
        formTitle: '이미지 프롬프트 추가',
        galleryTitle: '생성된 이미지',
        storagePath: 'genspark',
        outputType: 'image',
        // How to extract display text from input JSONB
        getTaskLabel: (task) => task.input?.prompt || '(프롬프트 없음)',
        // How to render gallery item
        renderGalleryItem: (task, index) => {
            const imageUrl = task.output?.image_url;
            const thumbnailUrl = task.output?.thumbnail_url || imageUrl;
            const prompt = task.input?.prompt || '';
            const workerName = getWorkerName(task.started_by);
            if (!imageUrl) return '';
            return `
                <div class="gallery-item" data-task-id="${task.id}" onclick="window.openGallerySlider(${index})" title="${escapeHtmlGlobal(prompt)}">
                    <img src="${thumbnailUrl}" alt="${escapeHtmlGlobal(prompt)}" loading="lazy">
                    ${workerName ? `<div class="gallery-worker" title="처리: ${escapeHtmlGlobal(workerName)}">${escapeHtmlGlobal(workerName)}</div>` : ''}
                </div>
            `;
        }
    },
    naver_blog: {
        id: 'naver_blog',
        name: 'Naver Blog',
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>',
        description: '블로그 자동 발행',
        formTitle: '블로그 포스트 추가',
        galleryTitle: '발행된 포스트',
        storagePath: 'naver_blog',
        outputType: 'link',
        getTaskLabel: (task) => task.input?.title || '(제목 없음)',
        renderGalleryItem: (task, index) => {
            const postUrl = task.output?.post_url;
            const title = task.input?.title || '제목 없음';
            if (!postUrl) return '';
            return `
                <div class="gallery-item gallery-link" data-task-id="${task.id}" onclick="window.open('${postUrl}', '_blank')">
                    <div class="link-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg></div>
                    <div class="link-title">${escapeHtmlGlobal(title)}</div>
                    <div class="link-url">${escapeHtmlGlobal(postUrl)}</div>
                </div>
            `;
        }
    },
    suno: {
        id: 'suno',
        name: 'Suno AI',
        icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>',
        description: 'AI 음악 생성',
        formTitle: '음악 프롬프트 추가',
        galleryTitle: '생성된 음악',
        storagePath: 'suno',
        outputType: 'audio',
        getTaskLabel: (task) => task.input?.prompt || '(프롬프트 없음)',
        renderGalleryItem: (task, index) => {
            const audioUrl = task.output?.audio_url;
            const prompt = task.input?.prompt || '';
            const duration = task.output?.duration || task.input?.duration || 0;
            if (!audioUrl) return '';
            return `
                <div class="gallery-item gallery-audio" data-task-id="${task.id}">
                    <div class="audio-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg></div>
                    <div class="audio-info">
                        <div class="audio-title">${escapeHtmlGlobal(prompt.substring(0, 50))}${prompt.length > 50 ? '...' : ''}</div>
                        <div class="audio-duration">${formatDuration(duration)}</div>
                    </div>
                    <audio controls src="${audioUrl}"></audio>
                </div>
            `;
        }
    }
};

// Current site state
let currentSite = 'genspark';

// Worker name cache (worker_id -> worker_name)
let workerNameCache = {};

// Current gallery tasks state
let currentGalleryTasks = [];
let currentSliderIndex = 0;

/**
 * Initialize site switcher
 */
function initSiteSwitcher() {
    const tabs = document.querySelectorAll('.site-tab');

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            const site = tab.dataset.site;
            if (site && site !== currentSite) {
                switchSite(site);
            }
        });
    });

    // Load saved site preference
    const savedSite = localStorage.getItem('current_site');
    if (savedSite && SITE_CONFIGS[savedSite]) {
        switchSite(savedSite, false);
    } else {
        updateSiteUI();
    }

    // Init slider events
    initSlider();
}

/**
 * Initialize Slider Event Listeners
 */
function initSlider() {
    const modal = document.getElementById('image-slider-modal');
    if (!modal) return;

    // Close buttons
    document.getElementById('slider-close')?.addEventListener('click', closeSlider);

    // Nav buttons
    document.getElementById('slider-prev')?.addEventListener('click', (e) => {
        e.stopPropagation();
        prevSlide();
    });
    document.getElementById('slider-next')?.addEventListener('click', (e) => {
        e.stopPropagation();
        nextSlide();
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        if (modal.classList.contains('hidden')) return;

        if (e.key === 'Escape') closeSlider();
        if (e.key === 'ArrowLeft') prevSlide();
        if (e.key === 'ArrowRight') nextSlide();
    });

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.classList.contains('slider-content') || e.target.classList.contains('slider-image-wrapper')) {
            closeSlider();
        }
    });
}

/**
 * Open slider at specific index
 */
window.openGallerySlider = function (index) {
    if (index < 0 || index >= currentGalleryTasks.length) return;

    const task = currentGalleryTasks[index];
    // Only open for image types
    if (task.site !== 'genspark') return;

    currentSliderIndex = index;
    const modal = document.getElementById('image-slider-modal');
    modal.classList.remove('hidden');
    modal.focus();

    updateSliderContent();
    document.body.style.overflow = 'hidden'; // Prevent scrolling
}

function closeSlider() {
    const modal = document.getElementById('image-slider-modal');
    modal.classList.add('hidden');
    document.body.style.overflow = '';
}

function updateSliderContent() {
    const task = currentGalleryTasks[currentSliderIndex];
    if (!task) return;

    const img = document.getElementById('slider-image');
    const promptEl = document.getElementById('slider-prompt');
    const workerEl = document.getElementById('slider-worker');
    const dateEl = document.getElementById('slider-date');
    const prevBtn = document.getElementById('slider-prev');
    const nextBtn = document.getElementById('slider-next');

    // Update Image
    img.src = task.output?.image_url || '';
    img.alt = task.input?.prompt || 'Generated Image';

    // Update Meta
    promptEl.textContent = task.input?.prompt || '';

    const workerName = getWorkerName(task.started_by);
    workerEl.textContent = workerName ? `By: ${workerName}` : '';

    dateEl.textContent = formatDateGlobal(task.created_at);

    // Update Buttons state
    prevBtn.disabled = currentSliderIndex <= 0;
    nextBtn.disabled = currentSliderIndex >= currentGalleryTasks.length - 1;
}

function nextSlide() {
    if (currentSliderIndex < currentGalleryTasks.length - 1) {
        currentSliderIndex++;
        updateSliderContent();
    }
}

function prevSlide() {
    if (currentSliderIndex > 0) {
        currentSliderIndex--;
        updateSliderContent();
    }
}

/**
 * Switch to a different site
 */
function switchSite(siteId, save = true) {
    if (!SITE_CONFIGS[siteId]) {
        console.error('Unknown site:', siteId);
        return;
    }

    currentSite = siteId;

    if (save) {
        localStorage.setItem('current_site', siteId);
    }

    updateSiteUI();

    // Notify app.js to refresh data
    if (typeof onSiteChanged === 'function') {
        onSiteChanged(siteId);
    }
}

/**
 * Update UI based on current site
 */
function updateSiteUI() {
    const config = SITE_CONFIGS[currentSite];

    // Update tab active states
    document.querySelectorAll('.site-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.site === currentSite);
    });

    // Update form visibility
    document.querySelectorAll('.site-form').forEach(form => {
        const formSite = form.id.replace('form-', '');
        form.classList.toggle('hidden', formSite !== currentSite);
    });

    // Update titles
    const addPanelTitle = document.getElementById('add-panel-title');
    if (addPanelTitle) {
        addPanelTitle.textContent = config.formTitle;
    }

    const galleryTitle = document.getElementById('gallery-title');
    if (galleryTitle) {
        galleryTitle.textContent = config.galleryTitle;
    }

    const currentSiteLabel = document.getElementById('current-site-label');
    if (currentSiteLabel) {
        currentSiteLabel.textContent = `(${config.name})`;
    }

    // Update app theme class
    document.body.className = `site-${currentSite}`;
}

/**
 * Get current site ID
 */
function getCurrentSite() {
    return currentSite;
}

/**
 * Get current site config
 */
function getSiteConfig(siteId = null) {
    return SITE_CONFIGS[siteId || currentSite];
}

/**
 * Get all site configs
 */
function getAllSiteConfigs() {
    return SITE_CONFIGS;
}

/**
 * Update site badge (pending count)
 */
function updateSiteBadge(siteId, count) {
    const badge = document.getElementById(`badge-${siteId}`);
    if (badge) {
        badge.textContent = count;
        badge.classList.toggle('has-pending', count > 0);
    }
}

/**
 * Get form data for current site
 */
function getFormData() {
    const config = SITE_CONFIGS[currentSite];

    switch (currentSite) {
        case 'genspark': {
            const promptsText = document.getElementById('genspark-prompts')?.value.trim() || '';
            const prompts = promptsText.split('\n').map(p => p.trim()).filter(p => p);
            const priority = parseInt(document.getElementById('genspark-priority')?.value || '0');
            const referenceInput = document.getElementById('genspark-reference');
            const referenceFiles = referenceInput?.files ? Array.from(referenceInput.files).slice(0, 5) : [];

            // 파일 개수 표시 업데이트
            const countEl = document.getElementById('genspark-reference-count');
            if (countEl) {
                countEl.textContent = referenceFiles.length > 0 ? `${referenceFiles.length}개 선택됨` : '';
            }

            return {
                type: 'batch',
                items: prompts.map(prompt => ({
                    input: { prompt },
                    priority
                })),
                referenceFiles,
                clear: () => {
                    document.getElementById('genspark-prompts').value = '';
                    document.getElementById('genspark-reference').value = '';
                    if (countEl) countEl.textContent = '';
                }
            };
        }

        case 'naver_blog': {
            const title = document.getElementById('naver-title')?.value.trim() || '';
            const content = document.getElementById('naver-content')?.value.trim() || '';
            const category = document.getElementById('naver-category')?.value.trim() || '';
            const priority = parseInt(document.getElementById('naver-priority')?.value || '0');

            if (!title || !content) {
                return { error: '제목과 내용을 모두 입력해주세요.' };
            }

            return {
                type: 'single',
                items: [{
                    input: { title, content, category },
                    priority
                }],
                clear: () => {
                    document.getElementById('naver-title').value = '';
                    document.getElementById('naver-content').value = '';
                    document.getElementById('naver-category').value = '';
                }
            };
        }

        case 'suno': {
            const prompt = document.getElementById('suno-prompt')?.value.trim() || '';
            const genre = document.getElementById('suno-genre')?.value || '';
            const duration = parseInt(document.getElementById('suno-duration')?.value || '30');
            const priority = parseInt(document.getElementById('suno-priority')?.value || '0');

            if (!prompt) {
                return { error: '음악 프롬프트를 입력해주세요.' };
            }

            return {
                type: 'single',
                items: [{
                    input: { prompt, genre, duration },
                    priority
                }],
                clear: () => {
                    document.getElementById('suno-prompt').value = '';
                    document.getElementById('suno-genre').value = '';
                    document.getElementById('suno-duration').value = '30';
                }
            };
        }

        default:
            return { error: '알 수 없는 사이트입니다.' };
    }
}

/**
 * Render task item for list view
 */
function renderTaskItem(task) {
    const config = SITE_CONFIGS[task.site] || SITE_CONFIGS.genspark;
    const label = config.getTaskLabel(task);
    const canReset = ['claimed', 'processing', 'failed'].includes(task.status);
    const canDuplicate = task.status === 'completed' && task.site === 'genspark';

    // Check if task has agent ID for recovery (Genspark only)
    const hasAgentId = task.site === 'genspark' && task.output?.genspark_agent_id;
    const canRecover = hasAgentId && ['processing', 'failed'].includes(task.status);

    // Prepare task data for duplicate (base64 encode to avoid quote issues)
    const taskDataForDuplicate = canDuplicate ? btoa(encodeURIComponent(JSON.stringify({
        prompt: task.input?.prompt || '',
        reference_image_urls: task.input?.reference_image_urls || []
    }))) : '';

    // Get worker name for assigned tasks
    const workerName = task.started_by ? getWorkerName(task.started_by) : (task.claimed_by ? getWorkerName(task.claimed_by) : null);
    const showWorker = workerName && ['claimed', 'processing', 'completed'].includes(task.status);

    return `
        <div class="task-item" data-task-id="${task.id}">
            <div class="task-status ${task.status}"></div>
            <div class="task-site-icon" title="${config.name}">${config.icon}</div>
            <div class="task-prompt" title="${escapeHtmlGlobal(label)}">${escapeHtmlGlobal(label)}</div>
            ${showWorker ? `<div class="task-worker" title="처리: ${escapeHtmlGlobal(workerName)}">${escapeHtmlGlobal(workerName)}</div>` : ''}
            <div class="task-time">${formatDateGlobal(task.created_at)}</div>
            ${canDuplicate ? `<button class="btn-duplicate" onclick="openRetryModal('${taskDataForDuplicate}')" title="프롬프트 수정 후 재시도"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg></button>` : ''}
            ${canRecover ? `<button class="btn-recover" onclick="tryRecoverTask('${task.id}', '${task.site}', '${task.output.genspark_agent_id}')" title="이미지 복구 시도"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"></path><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg></button>` : ''}
            ${canReset ? `<button class="btn-reset" onclick="resetTask('${task.id}', '${task.site}')" title="재시도"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 4v6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path></svg></button>` : ''}
        </div>
    `;
}

/**
 * Render gallery for current site with differential updates
 * Only updates DOM for new/removed items to preserve image cache
 */
function renderGallery(tasks) {
    const config = SITE_CONFIGS[currentSite];
    const gallery = document.getElementById('gallery');

    if (!gallery) return;

    if (!tasks || tasks.length === 0) {
        gallery.innerHTML = '<p class="empty-state">완료된 작업이 없습니다</p>';
        currentGalleryTasks = [];
        return;
    }

    // Set gallery class based on output type
    gallery.className = `gallery gallery-${config.outputType}`;

    // Get existing task IDs in DOM
    const existingItems = gallery.querySelectorAll('.gallery-item[data-task-id]');
    const existingIds = new Set(
        [...existingItems].map(el => el.dataset.taskId)
    );
    const newIds = new Set(tasks.map(t => t.id));

    // Check if we need full refresh (different set of tasks or first render)
    const needsFullRefresh = 
        existingIds.size === 0 || 
        existingIds.size !== newIds.size ||
        ![...newIds].every(id => existingIds.has(id));

    if (needsFullRefresh) {
        // Full refresh - but use DocumentFragment for efficiency
        const fragment = document.createDocumentFragment();
        
        tasks.forEach((task, index) => {
            const html = config.renderGalleryItem(task, index);
            if (html) {
                const wrapper = document.createElement('div');
                wrapper.innerHTML = html.trim();
                const element = wrapper.firstChild;
                if (element) {
                    element.dataset.taskId = task.id;
                    fragment.appendChild(element);
                }
            }
        });

        gallery.innerHTML = '';
        gallery.appendChild(fragment);
    } else {
        // Differential update - only update changed items
        // For now, just update the index for slider functionality
        existingItems.forEach((item, index) => {
            const taskId = item.dataset.taskId;
            const newIndex = tasks.findIndex(t => t.id === taskId);
            if (newIndex !== -1) {
                // Update onclick handler with new index
                item.onclick = () => window.openGallerySlider(newIndex);
            }
        });
    }

    // Save current tasks for slider
    currentGalleryTasks = tasks;
}

// Helper functions (global scope for use in template strings)
function escapeHtmlGlobal(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function formatDateGlobal(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return '방금 전';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;

    return date.toLocaleDateString('ko-KR');
}

function formatDuration(seconds) {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Update worker name cache
 */
function updateWorkerCache(workers) {
    if (!workers || !Array.isArray(workers)) return;
    workers.forEach(worker => {
        workerNameCache[worker.id] = worker.name || worker.id.substring(0, 8);
    });
}

/**
 * Get worker display name from cache
 */
function getWorkerName(workerId) {
    if (!workerId) return null;
    return workerNameCache[workerId] || workerId.substring(0, 12);
}

// Export functions for use by other modules
export {
    initSiteSwitcher,
    getCurrentSite,
    getSiteConfig,
    getAllSiteConfigs,
    updateSiteBadge,
    getFormData,
    renderTaskItem,
    renderGallery,
    updateWorkerCache,
    getWorkerName,
    escapeHtmlGlobal,
    formatDateGlobal
};
